import React, { forwardRef } from 'react';
import '@dtsl/css-select-menu/dist/index.css';
import PropTypes from 'prop-types';
import { SelectMenuProvider } from './context';
import { SelectMenuTypes } from './helper';
import SelectMenu from './SelectMenu';
import SelectMenuBody from './SelectMenu/SelectMenuBody/SelectMenuBody';
import SelectMenuControl from './SelectMenu/SelectMenuControl/SelectMenuControl';
import '@dtsl/css-utilities/dist/auto-positions/index.css';
import '@dtsl/css-utilities/dist/common-showcase/index.css';

// eslint-disable-next-line react/display-name
const SelectMenuWrapper = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    label = '',
    value = [],
    isError = false,
    readonly = false,
    disabled = false,
    removeTag = () => {},
    className = '',
    handleSearch = () => {},
    tooltipData = {},
    placeHolder = 'Select a condition',
    inputStyles = {},
    tabsLoading = [],
    isLoading = false,
    defaultOpen = false,
    searchInPanel = true,
    isSearchable = false,
    requiredLabel = false,
    controlClassName = '',
    placeholderClassName = '',
    specificationText = '',
    arrowClassName = '',
    inlineSelectMenuStyles = {},
    isControlButton = false,
    controlButtonMetaData = {},
    selectMenuType = SelectMenuTypes.SINGLE_SELECTION,
    isMobile = false,
    searchValue = '',
    dataTestId = '',
    maxTagWidth = 100,
    handleResetSearch = () => {},
    countTagWidth = 75,
    isSearchClearable = true,
    isSelectionClearable = false,
    isControlClearable = false,
    onClearControl = () => {},
    onSelectionClear = () => {},
    selectedFolderValue = {},
    isAbsoluteSelectMenu = false,
    autoPlacement = true,
    placement = '',
    isControlChip = false,
    controlChipMetaData = {},
    getPanelToggleState = () => {},
    onClick = () => {},
    enableVirtualization = true,
    virtualizationProps,
    onClose = () => {},
    identifier = '',
    scrollToSelectedValue = false,
    isSuccess = false,
    validationText = '',
    accessibleErrorText = '',
    accessibleSuccessText = '',
    size = 'medium',
    onFocus = () => {},
    onBlur = () => {},
    creatableButtonMetaData = {
      disabled: false,
      createLabelText: 'Create',
      handleCreateOption: () => {}
    },
    isCreatable = false,
    options = [],
    isExactMatch = false,
    helpButtonMetaData = null
  } = props;
  return /*#__PURE__*/React.createElement(SelectMenuProvider, {
    value: value,
    searchValue: searchValue,
    defaultOpen: defaultOpen,
    isMobile: isMobile,
    handleSearch: handleSearch,
    isLoading: isLoading,
    tabsLoading: tabsLoading,
    searchInPanel: searchInPanel,
    isSearchable: isSearchable,
    placeHolder: placeHolder,
    selectedFolderValue: selectedFolderValue,
    autoPlacement: autoPlacement,
    placement: placement,
    getPanelToggleState: getPanelToggleState,
    enableVirtualization: enableVirtualization,
    virtualizationProps: virtualizationProps,
    onClose: onClose,
    scrollToSelectedValue: scrollToSelectedValue
  }, /*#__PURE__*/React.createElement(SelectMenu, {
    ref: ref,
    label: label,
    isError: isError,
    disabled: disabled,
    className: className,
    inputStyles: inputStyles,
    placeHolder: placeHolder,
    isSearchable: isSearchable,
    requiredLabel: requiredLabel,
    isControlButton: isControlButton,
    isControlChip: isControlChip,
    specificationText: specificationText,
    inlineSelectMenuStyles: inlineSelectMenuStyles,
    isAbsoluteSelectMenu: isAbsoluteSelectMenu,
    value: value,
    isMobile: isMobile,
    autoPlacement: autoPlacement,
    identifier: identifier,
    isSuccess: isSuccess,
    validationText: validationText,
    accessibleErrorText: accessibleErrorText,
    accessibleSuccessText: accessibleSuccessText,
    helpButtonMetaData: helpButtonMetaData
  }, /*#__PURE__*/React.createElement(SelectMenuControl, {
    readonly: readonly,
    disabled: disabled,
    removeTag: removeTag,
    tooltipData: tooltipData,
    placeHolder: placeHolder,
    selectMenuType: selectMenuType,
    isSearchable: isSearchable,
    arrowClassName: arrowClassName,
    isControlButton: isControlButton,
    controlClassName: controlClassName,
    inputStyles: inputStyles,
    placeholderClassName: placeholderClassName,
    controlButtonMetaData: controlButtonMetaData,
    dataTestId: dataTestId,
    maxTagWidth: maxTagWidth,
    countTagWidth: countTagWidth,
    isSearchClearable: isSearchClearable,
    isControlClearable: isControlClearable,
    isSelectionClearable: isSelectionClearable,
    onClearControl: onClearControl,
    onSelectionClear: onSelectionClear,
    handleResetSearch: handleResetSearch,
    isControlChip: isControlChip,
    controlChipMetaData: controlChipMetaData,
    onClick: onClick,
    identifier: identifier,
    size: size,
    onFocus: onFocus,
    onBlur: onBlur,
    creatableButtonMetaData: creatableButtonMetaData,
    isCreatable: isCreatable,
    options: options,
    isExactMatch: isExactMatch,
    isError: isError
  }), /*#__PURE__*/React.createElement(SelectMenuBody, props)));
});
SelectMenuWrapper.propTypes = {
  selectMenuType: PropTypes.string,
  label: PropTypes.string,
  enableVirtualization: PropTypes.bool,
  virtualizationProps: PropTypes.shape({
    loadMoreData: PropTypes.func,
    listContainerHeight: PropTypes.number,
    subFolderListContainterHeight: PropTypes.number,
    overscan: PropTypes.number,
    isInfiniteLoading: PropTypes.bool
  }),
  size: PropTypes.string,
  defaultOpen: PropTypes.bool,
  isError: PropTypes.bool,
  isSuccess: PropTypes.bool,
  accessibleErrorText: PropTypes.string,
  accessibleSuccessText: PropTypes.string,
  validationText: PropTypes.string,
  onClick: PropTypes.func,
  value: PropTypes.array,
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,
  removeTag: PropTypes.func,
  className: PropTypes.string,
  placement: PropTypes.string,
  placeHolder: PropTypes.string,
  autoPlacement: PropTypes.bool,
  isSearchClearable: PropTypes.bool,
  isSelectionClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  searchInPanel: PropTypes.bool,
  searchInputProps: PropTypes.object,
  isControlClearable: PropTypes.bool,
  requiredLabel: PropTypes.bool,
  isMobile: PropTypes.bool,
  isLoading: PropTypes.bool,
  isAbsoluteSelectMenu: PropTypes.bool,
  isExactMatch: PropTypes.bool,
  isCreatable: PropTypes.bool,
  togglePanel: PropTypes.bool,
  isControlButton: PropTypes.bool,
  controlButtonMetaData: PropTypes.object,
  isControlChip: PropTypes.bool,
  controlChipMetaData: PropTypes.object,
  dataTestId: PropTypes.string,
  panelDataTestId: PropTypes.string,
  maxTagWidth: PropTypes.number,
  countTagWidth: PropTypes.number,
  menuClassName: PropTypes.string,
  arrowClassName: PropTypes.string,
  controlClassName: PropTypes.string,
  placeholderClassName: PropTypes.string,
  inputStyles: PropTypes.object,
  inlineSelectMenuPanelStyles: PropTypes.object,
  inlineSelectMenuStyles: PropTypes.object,
  specificationText: PropTypes.string,
  searchValue: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  selectAllLabel: PropTypes.string,
  unSelectAllLabel: PropTypes.string,
  selectAllIdentifier: PropTypes.string,
  selectedFolderValue: PropTypes.object,
  activeTabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  creatableButtonMetaData: PropTypes.object,
  tooltipData: PropTypes.object,
  options: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.string,
    value: PropTypes.any,
    icon: PropTypes.func,
    helpText: PropTypes.string,
    addtionalInfo: PropTypes.func,
    customLabelStyle: PropTypes.object
  })),
  items: PropTypes.array,
  tabMetaData: PropTypes.array,
  tabsLoading: PropTypes.array,
  children: PropTypes.node,
  onClearControl: PropTypes.func,
  onSelectionClear: PropTypes.func,
  handleResetSearch: PropTypes.func,
  handleParentNavigation: PropTypes.func,
  handleChildNavigation: PropTypes.func,
  addInfo: PropTypes.func,
  onMouseOver: PropTypes.func,
  onSelect: PropTypes.func,
  onTabSelect: PropTypes.func,
  getPanelToggleState: PropTypes.func,
  handleSearch: PropTypes.func,
  noOptionContent: PropTypes.node,
  stickyComponent: PropTypes.node,
  onClose: PropTypes.func,
  identifier: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  scrollToSelectedValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  helpButtonMetaData: PropTypes.object
};
export default SelectMenuWrapper;